import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import { fetchInventoryStockList } from '#/api/imsMgmt/inventoryStock/fetchList';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { buildInvStockPickerColumns } from '#/modules/imsMgmt/receipts/stockTransfer/data/invStockPickerColumns';
import useInvStockPickerListQuery from '#/modules/imsMgmt/receipts/stockTransfer/hooks/useInvStockPickerListQuery';
import { formatDisplayQty } from '#/utils/formatNumber';
import { inventoryStockListQueryKey } from '#/utils/queryKeys';

function StatCell({ label, value, emphasize = false }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 text-base font-semibold tabular-nums',
          emphasize ? 'text-red-600' : 'text-gray-900',
        )}
      >
        {value}
      </p>
    </div>
  );
}

StatCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  emphasize: PropTypes.bool,
};

function formatStatQty(value) {
  if (value == null || value === '') {
    return '—';
  }
  return formatDisplayQty(value);
}

const SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  stockNo: '庫存編號',
  warehouseId: '來源倉',
  locationId: '儲位',
};

function buildInitialSelection(initialSelectedRows = []) {
  const rowSelection = {};
  const selectedById = {};

  initialSelectedRows.forEach((row) => {
    const id = row?.id ?? row?.invStockId;
    if (id == null || id === '') {
      return;
    }
    const key = String(id);
    rowSelection[key] = true;
    selectedById[key] = row;
  });

  return { rowSelection, selectedById };
}

const InvStockPickerDialog = ({
  open,
  onOpenChange,
  excludeWarehouseId = null,
  mtrId = null,
  initialSelectedRows = [],
  onConfirm,
  title = '請選擇欲轉倉的庫存',
  description = '來源倉不得與轉倉倉別相同；多選後確認，已存在者不重複。',
  requiredQty,
  allocatedQty,
}) => {
  const showQtyStats = requiredQty !== undefined || allocatedQty !== undefined;
  const {
    listQuery,
    columnFilters,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  } = useInvStockPickerListQuery({ excludeWarehouseId, mtrId });

  const initialSelection = useMemo(
    () => buildInitialSelection(initialSelectedRows),
    [initialSelectedRows],
  );

  const [selectedById, setSelectedById] = useState(initialSelection.selectedById);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [warehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [locationLabelCache] = useState(createAsyncFilterLabelCache);

  const listQueryResult = useQuery({
    queryKey: inventoryStockListQueryKey(listQuery),
    queryFn: () => fetchInventoryStockList(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => {
    const source = listQueryResult.data?.data ?? [];
    const seen = new Set();

    return source.map((row, index) => {
      const rawId = row?.id;
      const base = (rawId == null || rawId === '')
        ? `row-${index}`
        : String(rawId);
      let id = base;
      if (seen.has(id)) {
        id = `${base}__${index}`;
      }
      seen.add(id);
      return { ...row, id };
    });
  }, [listQueryResult.data?.data]);

  const columns = useMemo(() => ([
    createSelectColumn({ enableMultiSelection: true }),
    ...buildInvStockPickerColumns({
      filters: columnFilters,
      onFilterChange: setFilter,
      allowWarehouseFilter: true,
      excludeWarehouseId,
      warehouseLabelCache,
      locationLabelCache,
    }),
  ]), [
    columnFilters,
    setFilter,
    excludeWarehouseId,
    warehouseLabelCache,
    locationLabelCache,
  ]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  /** status chip 用：warehouseId 只反映使用者選取（勿顯示 !=uuid）。 */
  const filterStatusListQuery = useMemo(() => ({
    ...listQuery,
    filters: columnFilters,
  }), [listQuery, columnFilters]);

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedById((prev) => {
      const next = { ...prev };

      rows.forEach((row) => {
        const id = String(row.id);
        if (selection[id]) {
          next[id] = row;
        } else {
          delete next[id];
        }
      });

      return next;
    });
  }, [rows]);

  function handleSearchSubmit() {
    setKeyword(searchKeyword);
  }

  function handleResetFilters() {
    setSearchKeyword('');
    resetQuery();
  }

  function handleConfirm() {
    onConfirm(Object.values(selectedById).filter(Boolean));
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      resetQuery();
      setSearchKeyword('');
    }
    onOpenChange(nextOpen);
  }

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2 sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={handleSearchSubmit}
        placeholder="搜尋庫存編號、料號或名稱"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="size-10 shrink-0 bg-panel text-foreground hover:bg-panel/90"
            aria-label="清除搜尋與篩選"
            onClick={handleResetFilters}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton={true}
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="確認選取"
      cancelText="取消"
      cancelAriaLabel="取消選庫存"
      confirmDisabled={listQueryResult.isLoading}
    >
      <div className="space-y-3">
        {showQtyStats ? (
          <div
            className={cn(
              'grid grid-cols-2 gap-2',
              'sticky top-0 z-10 border-b border-slate-200 bg-panel pb-3',
            )}
          >
            <StatCell
              label="需求量"
              value={formatStatQty(requiredQty)}
            />
            <StatCell
              label="配置量"
              value={formatStatQty(allocatedQty)}
            />
          </div>
        ) : null}
        {open ? (
          <ListTablePanel
            variant="plain"
            columns={columns}
            rows={rows}
            pagination={listQueryResult.data?.page ?? {
              current_page: 1,
              page_size: 10,
              total_count: 0,
              total_page: 1,
            }}
            isLoading={listQueryResult.isLoading}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            listQuery={filterStatusListQuery}
            filterStatusFields={filterStatusFields}
            sortFieldLabels={SORT_FIELD_LABELS}
            onFilterChange={setFilter}
            onKeywordClear={() => {
              setSearchKeyword('');
              setKeyword('');
            }}
            onSortRemove={removeSortAt}
            onSortingChange={handleSortingChange}
            enableMultiSelection
            paginationClassName="px-0 py-2"
            initialRowSelection={initialSelection.rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
          />
        ) : null}
      </div>
    </AppDialog>
  );
};

InvStockPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  excludeWarehouseId: PropTypes.string,
  mtrId: PropTypes.string,
  initialSelectedRows: PropTypes.arrayOf(PropTypes.object),
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  requiredQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allocatedQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default InvStockPickerDialog;
